/**
 * useAgentBridgeEnabled — the PERSISTED default-OFF agent-bridge enable preference.
 *
 * MIRRORS the shape of `useCoordinateSystem.ts`: a single persisted preference
 * read lazily from `localStorage` on first render and mirrored back on every
 * change so the choice survives a reload. It owns NOTHING else — there is NO
 * connect / socket / handle logic inside this hook (PRIORITY #2 dep-graph + the
 * security default live in `AgentBridgeToggle`, which wires `enabled` → the
 * connection store). This hook is a pure boolean preference.
 *
 * STORAGE KEY: `editor.agentBridgeEnabled` — distinct preference domain (the
 * agent-bridge enable flag), NOT the coordinate-system key. Stored as the string
 * "1" (enabled) / "0" (disabled).
 *
 * DEFAULT-OFF (INV-OFF, ПРИОРИТЕТ #1 + the security default of the whole slice):
 * absent OR any non-"1" value ⇒ `false`. Only the exact string "1" reads as ON,
 * so a corrupted / garbage value fails CLOSED to disabled — never to a surprise
 * auto-enable. The hook persisting `true` does NOT itself open a socket: a reload
 * with `enabled===true` re-prompts the pairing token via the dialog (the token is
 * a bearer secret and is NOT persisted), so there is no silent headless connect.
 *
 * SSR / no-window guard: mirrors `useCoordinateSystem` — the lazy initializer and
 * the persistence effect both no-op when `window` is undefined.
 */

import { useCallback, useEffect, useState } from "react";

/** The localStorage key for the persisted agent-bridge enable preference. */
export const STORAGE_KEY = "editor.agentBridgeEnabled";

export interface UseAgentBridgeEnabledResult {
  /** Whether the agent bridge is enabled (default OFF). */
  enabled: boolean;
  /** Set the enabled flag directly (persisted on change). */
  setEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  /** Flip the enabled flag (off ⇄ on). */
  toggle: () => void;
}

export function useAgentBridgeEnabled(): UseAgentBridgeEnabledResult {
  const [enabled, setEnabled] = useState<boolean>(() => {
    // DEFAULT-OFF: only the exact string "1" reads as enabled. Absent /
    // garbage / "0" ⇒ false (fail-closed to disabled).
    if (typeof window === "undefined") return false;
    // FAIL-CLOSED on blocked storage: `localStorage.getItem` THROWS in privacy
    // mode / sandboxed iframes / disabled cookies. This read runs during the
    // EditorHeader render — an uncaught throw would crash the whole editor. Catch
    // it and default OFF (the conservative security default) so a blocked-storage
    // browser still loads the editor, just with the toggle off.
    try {
      return window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      // Mirror back as "1"/"0" so the round-trip read above is exact. The WRITE
      // can also THROW under blocked storage (privacy mode / quota) — swallow it:
      // the in-memory `enabled` state still drives the session correctly, the
      // preference just won't survive a reload. Persistence is best-effort, never
      // fatal.
      try {
        window.localStorage.setItem(STORAGE_KEY, enabled ? "1" : "0");
      } catch {
        /* blocked / quota-exceeded storage — persistence is best-effort */
      }
    }
  }, [enabled]);

  const toggle = useCallback(() => {
    setEnabled((prev) => !prev);
  }, []);

  return { enabled, setEnabled, toggle };
}
